import { computed, type ComputedRef } from 'vue';
import {
    AmplitudeFeature,
    isDualGateEnabled,
} from '@/domain/featureToggles/amplitudeFeatureToggle';
import { useAmplitudeFeatures } from '@/stores/featureToggles/amplitudeFeaturesStore';
import { useOrganisationProfile } from '@/stores/organisationProfile/store';
import { OrganisationFeature } from '@/stores/organisationProfile/types';

/**
 * The one composed hip-spine gate: enabled only when the organisation feature and the
 * Amplitude feature toggle both agree. Every consumer reads this rather than either source,
 * so the AND rule exists in exactly one place.
 *
 * A stateless plain composable rather than a `defineStore`: it holds nothing and derives
 * everything from the two Model stores. It is co-located with the Amplitude store it depends
 * on so the whole feature-toggle slice lives in one folder.
 */
export function useHipSpineFeatureGate(): { enabled: ComputedRef<boolean> } {
    const organisationProfile = useOrganisationProfile();
    const amplitudeFeatures = useAmplitudeFeatures();

    const enabled = computed(() =>
        isDualGateEnabled(
            organisationProfile.features[OrganisationFeature.HipSpine],
            amplitudeFeatures.isEnabled(AmplitudeFeature.HipSpine),
        ),
    );

    return { enabled };
}
